import { brokersService } from '@/services';
import type { BulkUpdateBrokerProductsResult } from '@/services/brokers/types';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';
import {
  BULK_CHUNK_SIZE,
  chunkJobs,
  groupJobsByProducts,
  type BulkBrokerFailure,
  type BulkProductAction,
  type BulkProductJob,
  type BulkRunTotals,
} from '../utils/bulk-broker-products.util';

export interface BulkRunState {
  status: 'idle' | 'running' | 'cancelling' | 'done';
  action: BulkProductAction | null;
  /** Corretoras no total desta rodada. */
  total: number;
  done: number;
  totals: BulkRunTotals;
  failures: BulkBrokerFailure[];
  /** Erros globais do lote (produto fora do catálogo, falha do sync). */
  globalErrors: string[];
  cancelled: boolean;
}

const EMPTY_STATE: BulkRunState = {
  status: 'idle',
  action: null,
  total: 0,
  done: 0,
  totals: { updated: 0, skipped: 0, failed: 0, brokersOk: 0 },
  failures: [],
  globalErrors: [],
  cancelled: false,
};

/**
 * Orquestra a aplicação em massa.
 *
 * As chamadas são SEQUENCIAIS: o paralelismo já acontece dentro de cada lote,
 * no backend (concorrência 10 contra um pool de 20 conexões do InsureMO).
 * Disparar lotes em paralelo aqui multiplicaria isso e provocaria 429.
 */
export function useBulkBrokerProducts() {
  const queryClient = useQueryClient();
  const [state, setState] = useState<BulkRunState>(EMPTY_STATE);
  const cancelledRef = useRef(false);

  const reset = useCallback(() => {
    cancelledRef.current = false;
    setState(EMPTY_STATE);
  }, []);

  const cancel = useCallback(() => {
    cancelledRef.current = true;
    setState(prev =>
      prev.status === 'running' ? { ...prev, status: 'cancelling' } : prev
    );
  }, []);

  const run = useCallback(
    async (jobs: BulkProductJob[], action: BulkProductAction) => {
      if (jobs.length === 0) return;

      cancelledRef.current = false;

      // Um id por RODADA, não por lote: o `run` fatia a seleção em vários
      // requests, e a trilha de auditoria precisa saber que foi uma operação só.
      const operationId =
        globalThis.crypto?.randomUUID?.() ?? `op-${Date.now()}-${Math.random()}`;

      setState({
        ...EMPTY_STATE,
        status: 'running',
        action,
        total: jobs.length,
      });

      // Cada grupo tem a mesma lista de produtos — é o que o endpoint aceita por
      // chamada. Na primeira rodada é sempre um grupo só.
      const groups = groupJobsByProducts(jobs);

      const calls = groups.flatMap(group =>
        chunkJobs(
          group.brokers.map(broker => ({
            broker,
            productCodes: group.productCodes,
          })),
          BULK_CHUNK_SIZE
        ).map(chunk => ({ chunk, productCodes: group.productCodes }))
      );

      for (let index = 0; index < calls.length; index += 1) {
        if (cancelledRef.current) break;

        const { chunk, productCodes } = calls[index];
        const isLastCall = index === calls.length - 1;

        const response = await brokersService.bulkUpdateBrokerProducts({
          brokerIds: chunk.map(job => job.broker.code),
          productCodes,
          action,
          // O nome só existe aqui: resolvê-lo no backend custaria uma consulta
          // ao InsureMO por corretora. Sem ele a trilha mostraria o PartyCode.
          brokerLabels: Object.fromEntries(
            chunk.map(job => [job.broker.code, job.broker.name])
          ),
          operationId,
          // O sync é um deploy de datatable: uma vez no fim, não a cada lote.
          syncCatalog: isLastCall && !cancelledRef.current,
        });

        setState(prev => applyChunkResult(prev, chunk, productCodes, response));
      }

      // Uma invalidação só no fim: uma por lote dispararia N refetches durante a
      // barra de progresso. Resultado parcial também mexeu na base.
      queryClient.invalidateQueries({ queryKey: ['broker-products'] });

      setState(prev => ({
        ...prev,
        status: 'done',
        cancelled: cancelledRef.current,
      }));
    },
    [queryClient]
  );

  return { state, run, cancel, reset };
}

/** Acumula o resultado de um lote no estado da rodada. */
function applyChunkResult(
  prev: BulkRunState,
  chunk: BulkProductJob[],
  productCodes: string[],
  response: {
    success: boolean;
    data?: BulkUpdateBrokerProductsResult;
    error?: string;
  }
): BulkRunState {
  const done = prev.done + chunk.length;

  // Falha de transporte ou 4xx/5xx: o lote inteiro conta como falha, e cada
  // corretora entra na lista para a retentativa.
  if (!response.success || !response.data) {
    const message = response.error ?? 'Falha ao aplicar o lote';

    return {
      ...prev,
      done,
      totals: {
        ...prev.totals,
        failed: prev.totals.failed + chunk.length * productCodes.length,
      },
      failures: [
        ...prev.failures,
        ...chunk.map(job => ({
          brokerCode: job.broker.code,
          brokerName: job.broker.name,
          productCodes,
          message,
        })),
      ],
    };
  }

  const result = response.data;
  const nameByCode = new Map(
    chunk.map(job => [job.broker.code, job.broker.name])
  );

  const failures: BulkBrokerFailure[] = [];
  const globalErrors: string[] = [];

  result.errors.forEach(error => {
    if (!error.brokerId) {
      globalErrors.push(error.message);
      return;
    }

    failures.push({
      brokerCode: error.brokerId,
      brokerName: nameByCode.get(error.brokerId) ?? error.brokerId,
      // Erro sem produto = a corretora inteira falhou; reenviar tudo nela.
      productCodes: error.productCode ? [error.productCode] : productCodes,
      message: error.message,
    });
  });

  return {
    ...prev,
    done,
    totals: {
      updated: prev.totals.updated + result.updated,
      skipped: prev.totals.skipped + result.skipped,
      failed: prev.totals.failed + result.failed,
      brokersOk: prev.totals.brokersOk + result.brokersProcessed,
    },
    failures: [...prev.failures, ...failures],
    globalErrors: [...prev.globalErrors, ...globalErrors],
  };
}
